import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import { emissionValueWithDecimals } from '../../helpers/emission-value-with-decimals';
import { textifyDeclaredUnit } from '../../helpers/textify-numerical-footprint-values-with-their-units';
import { DeclaredUnit, DeclaredUnitToPlainSymbolsMap } from '../../interfaces/IComponentDetails';
import { EmissionSymbol } from '../../interfaces/IFootprint';

type EmissionValueWithSuffixProps = {
  emissionValue?: number | null;
  emissionValueDecimals?: number;
  declaredUnit?: DeclaredUnit;
  unitaryProductAmount?: number;
  emissionSymbol?: EmissionSymbol;
  abbreviateUnit?: boolean;
  suffixTextClassName?: string; // Suffix specific text class name
  numericalValueTextClassName?: string; // Numerical value specific text class name, e.g. bold, larger font, etc.
};
// Component to display emission value with its unit suffix after proper formatting and spacing
export const EmissionValueWithSuffix = (props: EmissionValueWithSuffixProps): JSX.Element => {
  const {
    emissionValue,
    emissionValueDecimals = 3,
    declaredUnit,
    unitaryProductAmount = 1,
    emissionSymbol,
    abbreviateUnit = false,
    suffixTextClassName = 'text-black',
    numericalValueTextClassName = 'text-black font-bold',
  } = props;

  const { t } = useTranslation();

  if (declaredUnit == null || emissionValue == null) {
    return <span className={suffixTextClassName}>{t('global.notSpecified')}</span>;
  }

  const denominatorAmountText =
    unitaryProductAmount === 1 ? '' : <span>{unitaryProductAmount}&nbsp;</span>;
  const denominatorUnitText = abbreviateUnit
    ? DeclaredUnitToPlainSymbolsMap[declaredUnit]
    : textifyDeclaredUnit(declaredUnit);

  return (
    <div className='flex flex-row flex-wrap align-items-center gap-1'>
      <span className='white-space-nowrap flex align-items-center'>
        {/* Numerical Emission Value */}
        <span className={numericalValueTextClassName}>
          {emissionValueWithDecimals(emissionValue, emissionValueDecimals)}
        </span>

        <span>&nbsp;</span>
        {/* Emission Symbol */}
        <span className={suffixTextClassName}>{emissionSymbol}</span>
      </span>

      <span className={classNames(suffixTextClassName)}>/</span>

      <span className={classNames('white-space-nowrap', suffixTextClassName)}>
        {denominatorAmountText}
        {denominatorUnitText}
      </span>
    </div>
  );
};

export default EmissionValueWithSuffix;
